import { useEffect } from 'react';
import { ArticleData } from '@/mocks/articles';

interface ArticleLayoutProps {
  article: ArticleData;
}

const SITE_URL = import.meta.env.VITE_SITE_URL;

export default function ArticleLayout({ article }: ArticleLayoutProps) {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.h1,
    description: article.metaDescription,
    author: { '@type': 'Organization', name: 'KakobuyPolska.hair' },
    datePublished: article.date,
    dateModified: article.date,
    inLanguage: 'pl',
    image: article.image,
    about: { '@type': 'Thing', name: 'Kakobuy Spreadsheet' },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Strona główna', item: SITE_URL + '/' },
      { '@type': 'ListItem', position: 2, name: article.h1, item: `${SITE_URL}/artykul/${article.slug}` },
    ],
  };

  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />

      <main className="min-h-screen bg-background-50">
        <nav className="sticky top-0 z-40 bg-background-50 border-b border-background-200/70">
          <div className="max-w-4xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
            <a href="/" className="text-lg font-heading font-bold text-foreground-900 no-underline cursor-pointer">
              Kakobuy<span className="text-primary-500">Polska</span>
            </a>
            <nav className="hidden sm:flex items-center gap-1 text-xs text-foreground-500">
              <a href="/" className="hover:text-foreground-800 transition-colors whitespace-nowrap cursor-pointer no-underline">
                Strona główna
              </a>
              <span>/</span>
              <span className="text-foreground-700 whitespace-nowrap">{article.category}</span>
            </nav>
            <a href="/" className="sm:hidden text-xs text-foreground-500 hover:text-foreground-800 transition-colors cursor-pointer no-underline">
              <i className="ri-arrow-left-line"></i> Powrót
            </a>
          </div>
        </nav>

        <article className="max-w-3xl mx-auto px-4 md:px-6 py-8 md:py-14">
          <div className="mb-8 md:mb-10 rounded-lg overflow-hidden">
            <img
              src={article.image}
              alt={article.h1}
              className="w-full h-48 md:h-64 object-cover object-top"
              title={`${article.h1} - Kakobuy Spreadsheet Polska`}
            />
          </div>

          <header className="mb-10 md:mb-14">
            <div className="flex flex-wrap items-center gap-2 mb-4">
              <span className="px-2.5 py-1 text-xs rounded-full bg-accent-100 text-accent-700 font-medium">
                {article.category}
              </span>
              <span className="text-xs text-foreground-400">
                {new Date(article.date).toLocaleDateString('pl-PL', { year: 'numeric', month: 'long', day: 'numeric' })}
              </span>
            </div>

            <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-900 leading-tight mb-4">
              {article.h1}
            </h1>

            <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
              {article.excerpt}
            </p>
          </header>

          <div className="space-y-10">
            {article.sections.map((section, sIdx) => (
              <section key={sIdx}>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-4">
                  {section.heading}
                </h2>

                {section.type === 'checklist' ? (
                  <ul className="space-y-3">
                    {section.content.map((item, iIdx) => (
                      <li key={iIdx} className="flex gap-3">
                        <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5 rounded-full bg-accent-100">
                          <i className="ri-check-line text-xs text-accent-600"></i>
                        </div>
                        <div>
                          <p className="text-sm text-foreground-600 leading-relaxed">{item}</p>
                        </div>
                      </li>
                    ))}
                  </ul>
                ) : section.type === 'tips' ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {section.content.map((item, iIdx) => (
                      <div key={iIdx} className="flex gap-3 p-4 bg-background-100/70 rounded-lg border border-background-200/50">
                        <div className="w-8 h-8 flex items-center justify-center flex-shrink-0 rounded-md bg-primary-100">
                          <i className="ri-price-tag-3-line text-sm text-primary-600"></i>
                        </div>
                        <p className="text-sm text-foreground-600 leading-relaxed">{item}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="space-y-3">
                    {section.content.map((para, iIdx) => (
                      <p key={iIdx} className="text-sm md:text-base text-foreground-600 leading-relaxed">
                        {para}
                      </p>
                    ))}
                  </div>
                )}
              </section>
            ))}

            <div className="pt-6 border-t border-background-200/70">
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <a
                  href="/"
                  className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-primary-600 border border-primary-300 rounded-md hover:bg-primary-50 transition-colors whitespace-nowrap cursor-pointer no-underline"
                >
                  <i className="ri-arrow-left-line"></i>
                  Powrót do strony głównej
                </a>
                <a
                  href={article.ctaUrl}
                  target="_blank"
                  rel="noopener nofollow"
                  className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold bg-primary-500 hover:bg-primary-600 text-background-50 rounded-md transition-colors whitespace-nowrap cursor-pointer no-underline"
                >
                  {article.ctaText}
                  <i className="ri-arrow-right-up-line"></i>
                </a>
              </div>

              <div className="mt-8 p-4 bg-secondary-50 rounded-lg border border-secondary-200/50">
                <p className="text-xs text-foreground-500 text-center leading-relaxed">
                  <strong className="text-foreground-600">Przydatne linki:</strong>{' '}
                  <a href="/" className="text-accent-600 hover:text-accent-700 underline cursor-pointer">
                    Strona główna Kakobuy Spreadsheet Polska
                  </a>
                  {' · '}
                  <a href="/#categories" className="text-accent-600 hover:text-accent-700 underline cursor-pointer">
                    Kategorie produktów
                  </a>
                  {' · '}
                  <a href="/#safety" className="text-accent-600 hover:text-accent-700 underline cursor-pointer">
                    Bezpieczeństwo zakupów
                  </a>
                  {' · '}
                  <a href="/#faq" className="text-accent-600 hover:text-accent-700 underline cursor-pointer">
                    FAQ — najczęściej zadawane pytania
                  </a>
                </p>
              </div>
            </div>
          </div>
        </article>

        <footer className="py-8 bg-secondary-100 border-t border-secondary-200/70">
          <div className="max-w-3xl mx-auto px-4 md:px-6 text-center">
            <a href="/" className="text-base font-heading font-bold text-foreground-900 no-underline cursor-pointer">
              Kakobuy<span className="text-primary-500">Polska</span>
            </a>
            <p className="text-xs text-foreground-400 mt-2">
              © 2026 KakobuyPolska.hair. Wszelkie prawa zastrzeżone.
            </p>
            <p className="text-xs text-foreground-400 mt-1">
              Ta strona nie jest powiązana z Kakobuy. Jest to niezależny projekt dostarczający informacji i linków do produktów.
            </p>
          </div>
        </footer>
      </main>
    </>
  );
}